import { RefObject, useEffect } from "react";
import { addUserEventSourceListener } from "@/common/hooks/event-source/useUserApiEventSource.ts";
import { POINT_UPDATE } from "@/common/sse-api-event-source-message-types";
import { ExplorerItem, ExplorerItems } from "@/types/types.ts";
import useExplorerItems from "@/common/hooks/useExplorerItems.ts";
import { Point } from "peaks.js";
import { updatePointInPeaks } from "@/features/Waveform/utils.ts";
import useCurrentExplorerItemId from "@/common/hooks/useCurrentExplorerItemId.ts";

function usePointUpdate(eventSourceRef: RefObject<EventSource | null>) {
  const [currentExplorerItemId] = useCurrentExplorerItemId();
  const [, setExplorerItems] = useExplorerItems();

  // POINT_UPDATE Listener
  useEffect(() => {
    if (!eventSourceRef.current) {
      return;
    }

    const handlePointUpdate = (e: MessageEvent) => {
      const { explorerItemId, point } = JSON.parse(e.data) as {
        explorerItemId: string;
        point: Point;
      };

      setExplorerItems((prevItems: ExplorerItems) => {
        const itemToUpdate = prevItems[explorerItemId];
        if (!itemToUpdate) {
          return prevItems;
        }

        const updatedPoints = {
          ...itemToUpdate.track.points,
          [point.id as string]: point,
        };
        const track = {
          ...itemToUpdate.track,
          points: updatedPoints,
        };

        const updatedItem: ExplorerItem = {
          ...itemToUpdate,
          track,
        };

        return {
          ...prevItems,
          [explorerItemId]: updatedItem,
        };
      });

      if (currentExplorerItemId === explorerItemId) {
        updatePointInPeaks(point);
      }
    };

    const removeListener = addUserEventSourceListener(
      eventSourceRef.current,
      POINT_UPDATE,
      handlePointUpdate,
    );

    // Cleanup
    return () => {
      removeListener();
    };
  }, [
    currentExplorerItemId,
    eventSourceRef,
    setExplorerItems
  ]);
}

export default usePointUpdate;
